import "@theme/load-more";

/** Named view transition for filter-driven collection grid swaps (see utilities.css). */
const FILTER_GRID_VIEW_TRANSITION_NAME = "filter-collection-grid";

/** Applied to the grid mount while filter results are fetching; dims cards via utilities.css */
const FILTER_RESULTS_PENDING_CLASS = "filter-results-pending";

class PriceRangeSlider extends HTMLElement {
  constructor() {
    super();
    this.rangeInputs = this.querySelectorAll('[range-input] input[type="range"]');
    this.range = this.querySelector('[slider] [progress-bar]');
    this.minInput = this.querySelector('[min-price-filter]'); // hidden gte input the filter reads
    this.maxInput = this.querySelector('[max-price-filter]'); // hidden lte input the filter reads
    this.priceInput = this.querySelectorAll('[price-input-show]'); // visible, editable min/max fields
    this.priceGap = 1;
  }

  connectedCallback() {
    if (this.rangeInputs.length !== 2 || !this.range) return;
    this.max = parseInt(this.rangeInputs[1].max, 10) || 0;
    this.updateFill();
    this.attachEventListeners();
  }

  clamp(value) {
    if (Number.isNaN(value)) return 0;
    return Math.max(0, Math.min(value, this.max));
  }

  updateFill() {
    const minVal = this.clamp(parseInt(this.rangeInputs[0].value, 10));
    const maxVal = this.clamp(parseInt(this.rangeInputs[1].value, 10));
    this.range.style.left = (this.max ? (minVal / this.max) * 100 : 0) + "%";
    this.range.style.right = (this.max ? 100 - (maxVal / this.max) * 100 : 0) + "%";
  }

  // Push into the hidden gte/lte inputs, blank at the bounds so a full range applies no filter.
  syncHidden(minVal, maxVal) {
    if (this.minInput) this.minInput.value = minVal > 0 ? minVal : "";
    if (this.maxInput) this.maxInput.value = maxVal < this.max ? maxVal : "";
  }

  setValues(minVal, maxVal, updateVisible, updateRange) {
    minVal = this.clamp(minVal);
    maxVal = this.clamp(maxVal);
    if (maxVal - minVal < this.priceGap) return;
    if (updateRange) {
      this.rangeInputs[0].value = minVal;
      this.rangeInputs[1].value = maxVal;
    }
    if (updateVisible) {
      if (this.priceInput[0]) this.priceInput[0].value = minVal;
      if (this.priceInput[1]) this.priceInput[1].value = maxVal;
    }
    this.updateFill();
    this.syncHidden(minVal, maxVal);
  }

  attachEventListeners() {
    // Typing in the visible price fields — update the slider + hidden inputs (leave the field
    // the user is editing untouched). filters.js applies on the field's `change` (blur/enter).
    this.priceInput.forEach((input) => {
      input.addEventListener("input", () => {
        const minVal = parseInt(this.priceInput[0]?.value, 10);
        const maxRaw = parseInt(this.priceInput[1]?.value, 10);
        const maxVal = Number.isNaN(maxRaw) ? this.max : maxRaw;
        this.setValues(minVal, maxVal, false, true);
      });
    });

    // Dragging the range handles — mirror into the visible fields + hidden inputs.
    this.rangeInputs.forEach((input) => {
      input.addEventListener("input", (e) => {
        let minVal = parseInt(this.rangeInputs[0].value, 10);
        let maxVal = parseInt(this.rangeInputs[1].value, 10);
        if (maxVal - minVal < this.priceGap) {
          if (e.target.classList.contains("range-min")) {
            this.rangeInputs[0].value = maxVal - this.priceGap;
          } else {
            this.rangeInputs[1].value = minVal + this.priceGap;
          }
          minVal = parseInt(this.rangeInputs[0].value, 10);
          maxVal = parseInt(this.rangeInputs[1].value, 10);
        }
        this.setValues(minVal, maxVal, true, false);
      });
    });
  }
}

customElements.define('price-range-slider', PriceRangeSlider);

class CustomFilter extends HTMLElement {
  constructor() {
    super();
    this.control = this.querySelector('.filter-heading');
    this.body = this.querySelector('.filter-body');
  }

  connectedCallback() {
    if (!this.control || !this.body) {
      console.warn('CustomFilter: Missing required elements (.filter-heading or .filter-body)');
      return;
    }

    this.attachEventListeners();
  }

  // Panels are independent: opening one leaves the others alone, and clicking elsewhere on the page
  // doesn't collapse the sidebar. Shoppers routinely narrow by two or three facets at once, and the
  // old one-at-a-time behaviour hid the choices they'd already made. Each heading toggles only its
  // own panel, so anything opened stays open until it's clicked shut.
  attachEventListeners() {
    this.control.addEventListener('click', () => {
      this.classList.toggle('open');
    });
  }

  disconnectedCallback() {
    if (this.control) {
      this.control.removeEventListener('click', this.handleClick);
    }
  }
}

customElements.define('custom-filter', CustomFilter);

class Filters extends HTMLElement {
  constructor() {
    super();
    this.mobileControl = this.querySelector('.mobile-filter-heading');
    this.filterBody = this.querySelector('.custom-filter-wrapper');
    this.filterInputs = this.querySelectorAll('.filter-container-wrapper select, .filter-container-wrapper input:not([data-filter-search])');
    this.filterForm = this.querySelector('.filter-container');
    const filterWrapper = this.closest('.filter-wrapper');
    this.resetFilters = (filterWrapper || this).querySelectorAll('[data-reset-filters]');
    this.selectedFiltersHtml = this.querySelectorAll('[selected-filter], .selected-filter[data-param-remove]');
    this.collectionGrid = document.querySelector('[data-filter-grid]');
  }

  /** Inner grid container (`[grid]`) used for pending visual state on filter fetch. */
  getResultsOverlayMount() {
    if (!this.collectionGrid) return null;
    const scoped =
      this.collectionGrid.querySelector('.collection-grid.grid') ||
      this.collectionGrid.querySelector('.product-grid[grid]');
    return scoped || this.collectionGrid;
  }

  /**
   * Mount for filter-driven View Transitions only (not the outer `[data-filter-grid]` section).
   * A non-none `view-transition-name` on the section creates a stacking context, so the mobile
   * fixed filter shell no longer stacks above later page sections.
   */
  getFilterViewTransitionTarget() {
    if (!this.collectionGrid) return null;
    return (
      this.collectionGrid.querySelector('.collection-grid.grid') ||
      this.collectionGrid.querySelector('.product-grid[grid]') ||
      this.collectionGrid.querySelector('[grid]')
    );
  }

  /** Named transition on the product grid only; never on the section root. */
  setFilterGridViewTransitionName() {
    if (!this.collectionGrid) return;
    this.collectionGrid.style.removeProperty('view-transition-name');
    const target = this.getFilterViewTransitionTarget();
    if (target) {
      target.style.viewTransitionName = FILTER_GRID_VIEW_TRANSITION_NAME;
    }
  }

  /** Wrapper around filter UI for loading pointer-events. */
  getFilterPointerRoot() {
    return this.closest('.vertical-filters, .horizontal-filters, .filter-wrapper') || this;
  }

  connectedCallback() {
    this.attachMobileFilterListeners();
    this.attachFormSubmissionListeners();
    this.attachSelectedFilterListeners();
    this.attachResetFilterListener();
  }

  isMobileOrTabletViewport() {
    return window.matchMedia('(max-width: 991.98px)').matches;
  }

  getCollectionGridScrollTop() {
    if (!this.collectionGrid) return 0;
    const rect = this.collectionGrid.getBoundingClientRect();
    return Math.max(0, Math.round(window.scrollY + rect.top - 120));
  }

  prefersFilterViewTransition() {
    // TEMP PERF TEST — forces the view-transition crossfade off so the grid swap is instant.
    // Remove this line to restore the animation.
    return false;
    return (
      typeof document.startViewTransition === "function" &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }

  attachMobileFilterListeners() {
    if (this.mobileControl && this.filterBody) {
      this.mobileControl.addEventListener('click', () => {
        // Toggle mobile control open class
        if (this.mobileControl.classList.contains('open')) {
          this.mobileControl.classList.remove('open');
        } else {
          this.mobileControl.classList.add('open');
        }

        // Toggle filter body open class
        if (this.filterBody.classList.contains('open')) {
          this.filterBody.classList.remove('open');
        } else {
          this.filterBody.classList.add('open');
        }
      });
    }
  }

  attachFormSubmissionListeners() {
    if (this.filterInputs && this.filterForm && this.collectionGrid) {
      // Get the apply button you added in Liquid
      const applyBtn = this.filterForm.querySelector('.apply-filters-btn');

      // Ticking a box never fetches on its own, on any viewport — the shopper builds up a
      // selection and commits it with Apply. Desktop used to auto-submit on change; it now
      // matches mobile so both share one mental model (and one round trip per selection, not
      // one per checkbox).
      this.filterInputs.forEach((input) => {
        input.addEventListener('change', (event) => {
          event.stopPropagation();
          this.getResultsOverlayMount()?.classList.remove(FILTER_RESULTS_PENDING_CLASS);
          // Keep the sticky "View all N results" button honest about the pending selection.
          this.scheduleResultCountRefresh();
        });
      });

      // Explicit form submit / apply button should trigger filtering on all viewports.
      this.filterForm.addEventListener('submit', (event) => {
        event.preventDefault();
        this.handleFilterChange();
      });

      if (applyBtn) {
        applyBtn.addEventListener('click', (event) => {
          event.preventDefault();
          this.handleFilterChange();
        });
      }
    }
  }

  /**
   * Refresh the sticky button's "View all N results" count for the CURRENT (uncommitted)
   * selection.
   *
   * Debounced, because ticking several boxes in a row would otherwise fire a request each. Only
   * the newest request is allowed to write — an AbortController cancels the one in flight — so a
   * slow earlier response can't overwrite a newer count.
   *
   * The count is read out of the same section render the apply path uses, rather than a separate
   * endpoint, so it can't disagree with what applying actually produces.
   */
  scheduleResultCountRefresh() {
    const target = document.querySelector('[data-filter-result-count]');
    if (!target) return;

    window.clearTimeout(this._countRefreshTimer);
    this._countRefreshTimer = window.setTimeout(() => this.refreshResultCount(target), 250);
  }

  async refreshResultCount(target) {
    this._countRequest?.abort();
    this._countRequest = new AbortController();

    const button = target.closest('[data-filter-apply]');
    button?.classList.add('is-counting');

    try {
      const params = this.collectFilterParams();
      const { url, sectionName } = this.buildFilteredUrl(params);
      if (!sectionName) return;

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
        signal: this._countRequest.signal,
      });
      if (!response.ok) return;

      const data = await response.json();
      const markup = data?.[sectionName];
      if (!markup) return;

      const doc = new DOMParser().parseFromString(markup, 'text/html');

      const next = doc.querySelector('[data-filter-result-count]');
      if (next) target.textContent = next.textContent.trim();

      // The same response carries the whole re-rendered sidebar, so the per-option counts come
      // free — no second request.
      this.syncOptionCounts(doc);
    } catch (error) {
      /* Aborted by a newer request, or the network failed — leave the last good count in place. */
    } finally {
      button?.classList.remove('is-counting');
    }
  }

  /**
   * Update every option's own count ("Emerald 33") to reflect the pending selection, from the
   * markup already fetched for the sticky button's total.
   *
   * Only counts and availability are copied across — `checked` is deliberately never touched,
   * because the shopper's uncommitted selection is the thing driving this request in the first
   * place and rewriting it would fight them mid-interaction.
   *
   * Options are matched on name + value rather than by index or a CSS selector: facet values are
   * raw strings like `gid://shopify/Metaobject/155535802645`, which don't survive being dropped
   * into a selector, and the option ORDER can differ once Shopify drops zero-count values.
   */
  syncOptionCounts(doc) {
    if (!this.filterForm) return;

    const key = (input) => `${input.name}\n${input.value}`;
    const isOption = (input) =>
      (input.type === 'checkbox' || input.type === 'radio') &&
      input.name &&
      // Price-band radios are slider helpers, not facets — no counts, nothing to sync.
      !input.hasAttribute('data-price-band');

    const incoming = new Map();
    doc.querySelectorAll('.filter-container input[name]').forEach((input) => {
      if (!isOption(input)) return;
      const label = input.closest('label');
      const countEl = label?.querySelector('.filter-option-count');
      if (!countEl) return;
      incoming.set(key(input), {
        count: countEl.textContent.trim(),
        disabled: input.disabled,
        dimmed: (label.getAttribute('style') || '').includes('opacity'),
      });
    });

    if (!incoming.size) return;

    this.filterForm.querySelectorAll('input[name]').forEach((input) => {
      if (!isOption(input)) return;
      const fresh = incoming.get(key(input));
      if (!fresh) return;

      const label = input.closest('label');
      const countEl = label?.querySelector('.filter-option-count');
      if (!label || !countEl) return;

      countEl.textContent = fresh.count;

      // Never disable or dim something the shopper has ticked — they'd be left unable to untick
      // their own pending choice.
      const mayDisable = !input.checked;
      input.disabled = mayDisable && fresh.disabled;
      label.style.opacity = mayDisable && fresh.dimmed ? '0.5' : '';
    });
  }

  /**
   * Read the live form state into URLSearchParams. Shared by the apply path and the sticky
   * button's count preview so the two can never disagree about what's selected.
   */
  collectFilterParams() {
    const params = new URLSearchParams();
    const formElements = this.filterForm.querySelectorAll('input, select, textarea');

    formElements.forEach((element) => {
      const name = element.name;
      const value = element.value;
      if (!name || name.trim() === '') return;

      let shouldInclude = false;
      switch (element.type) {
        case 'checkbox':
        case 'radio':
          shouldInclude = element.checked;
          break;
        default:
          // selects, text, number, range — include anything with a non-empty value
          shouldInclude = value && value.trim() !== '';
          break;
      }

      if (shouldInclude) params.append(name, value);
    });

    return params;
  }

  /**
   * Current page URL rebuilt around `params`. Every `filter.*` key plus sort/type/q/page is
   * cleared first, so removing a filter actually drops it rather than stacking another value.
   */
  buildFilteredUrl(params, { includeSection = true } = {}) {
    const url = new URL(window.location.href);

    const keysToDelete = [];
    for (const [paramKey] of url.searchParams) {
      if (
        paramKey === 'sort_by' ||
        paramKey === 'type' ||
        paramKey === 'q' ||
        paramKey === 'page' ||
        paramKey.startsWith('filter.')
      ) {
        keysToDelete.push(paramKey);
      }
    }
    keysToDelete.forEach((key) => url.searchParams.delete(key));

    for (const [key, value] of params) {
      if (value && value.trim() !== '') url.searchParams.append(key, value);
    }

    const sectionName = this.getSectionName();
    if (includeSection && sectionName) url.searchParams.set('sections', sectionName);

    return { url, sectionName };
  }

  async handleFilterChange() {

    if (!this.filterForm || !this.collectionGrid) {
      return;
    }

    // Clear stored load more count since we're filtering
    this.clearLoadMoreState();

    // Show loading state
    this.showLoadingState();

    try {
      const params = this.collectFilterParams();
      const { url: currentUrl, sectionName } = this.buildFilteredUrl(params);


      // Check if we're in local development environment
      const isLocalDev = window.location.hostname === '127.0.0.1' ||
                        window.location.hostname === 'localhost' ||
                        window.location.port !== '';


      // Try section rendering first (skip in local dev as it often fails)
      let sectionsWorked = false;
      if (sectionName && !isLocalDev) {
        try {
          const response = await fetch(currentUrl.toString(), {
            method: 'GET',
            headers: {
              'X-Requested-With': 'XMLHttpRequest'
            },
            credentials: 'same-origin',
          });


          if (response.ok) {
            const data = await response.json();

            if (data && data[sectionName]) {
              this.updateCollectionGrid(data[sectionName]);
              this.updateBrowserUrl(params);
              sectionsWorked = true;
            }
          } else {
            await response.text();
          }
        } catch {
          /* Section render request failed */
        }
      }

      // If section rendering didn't work, fall back to full page request
      if (!sectionsWorked) {

        // Remove sections parameter for full page request
        const fullPageUrl = new URL(currentUrl);
        fullPageUrl.searchParams.delete('sections');


        const fullResponse = await fetch(fullPageUrl.toString(), {
          method: 'GET',
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'same-origin',
        });

        if (!fullResponse.ok) {
          console.error(`Full page request failed! status: ${fullResponse.status}`);
          throw new Error(`HTTP error! status: ${fullResponse.status}`);
        }

        const html = await fullResponse.text();

        // Parse the HTML and extract the section
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newSection = doc.querySelector('[data-filter-grid]');


        if (newSection) {
          this.updateCollectionGrid(newSection.outerHTML);
          this.updateBrowserUrl(params);
        } else {
          // Try alternative selectors
          const altSection = doc.querySelector('section[id*="collection"]') || doc.querySelector('section[id*="search"]');

          if (altSection) {
            this.updateCollectionGrid(altSection.outerHTML);
            this.updateBrowserUrl(params);
          } else {
            console.warn("❌ Could not find any suitable section in full page response");
            this.hideLoadingState();
          }
        }
      }
    } catch (error) {
      console.error("Filter request failed:", error);
      this.hideLoadingState();
    }
  }

  updateBrowserUrl(params) {

    // Update URL without page reload (clean URL without sections parameter)
    const newUrl = new URL(window.location.href);

    // Clear existing filter/search/sort/page parameters before rebuilding from form state.
    const keysToDelete = [];
    for (const [paramKey] of newUrl.searchParams) {
      if (
        paramKey === 'sort_by' ||
        paramKey === 'type' ||
        paramKey === 'q' ||
        paramKey === 'page' ||
        paramKey.startsWith('filter.')
      ) {
        keysToDelete.push(paramKey);
      }
    }
    keysToDelete.forEach((key) => {
      newUrl.searchParams.delete(key);
    });

    // Remove sections parameter (used for API calls only)
    newUrl.searchParams.delete('sections');


    // Add new filter parameters
    for (const [key, value] of params) {
      if (value && value.trim() !== '') {
        newUrl.searchParams.append(key, value);
      }
    }


    // Update the browser URL
    window.history.pushState({}, '', newUrl.toString());

  }

  clearLoadMoreState() {
    // Clear localStorage for any grid-load-more elements on the page
    try {
      const gridElements = document.querySelectorAll('grid-load-more');
      gridElements.forEach(element => {
        if (typeof element.clearStoredCount === 'function') {
          element.clearStoredCount();
        }
      });
    } catch (error) {
      console.warn('Failed to clear load more state:', error);
    }
  }

  showLoadingState() {
    const mount = this.getResultsOverlayMount();
    if (mount) {
      mount.classList.add(FILTER_RESULTS_PENDING_CLASS);
      mount.querySelector(".filter-loading-overlay")?.remove();
    }

    this.setFormInputsDisabled(true);

    const filterRoot = this.getFilterPointerRoot();
    if (filterRoot) filterRoot.style.pointerEvents = "none";
  }

  hideLoadingState() {
    const mount = this.getResultsOverlayMount();
    if (mount) {
      mount.classList.remove(FILTER_RESULTS_PENDING_CLASS);
      mount.querySelector(".filter-loading-overlay")?.remove();
    }

    this.setFormInputsDisabled(false);

    const filterRoot = this.getFilterPointerRoot();
    if (filterRoot) filterRoot.style.pointerEvents = "";
  }

  setFormInputsDisabled(disabled) {
    if (this.filterInputs) {
      this.filterInputs.forEach(input => {
        input.disabled = disabled;
      });
    }
  }

  getSectionName() {

    // Method 1: Read the real section.id rendered into the data-filter-grid element itself
    const sectionElement = document.querySelector('[data-filter-grid]');

    if (sectionElement?.dataset.sectionId) {
      return sectionElement.dataset.sectionId;
    }

    if (sectionElement) {
      const section = sectionElement.closest('section[id]');

      if (section && section.id) {
        // Remove 'shopify-section-' prefix if present
        const sectionId = section.id.replace(/^shopify-section-/, '');
        return sectionId;
      }
    }

    // Method 2: Check URL patterns
    const pathname = window.location.pathname;

    if (pathname.includes('/search')) {
      return 'template--search';
    } else if (pathname.includes('/collections/')) {
      return 'template--collection';
    }

    // Method 3: Try common section names by searching DOM
    const possibleSections = [
      'template--collection',
      'template--search',
      'collection',
      'search'
    ];

    for (const sectionName of possibleSections) {
      const variations = [
        `#${sectionName}`,
        `#shopify-section-${sectionName}`,
        `section[id="${sectionName}"]`,
        `section[id="shopify-section-${sectionName}"]`
      ];

      for (const selector of variations) {
        const element = document.querySelector(selector);
        if (element) {
          return sectionName;
        }
      }
    }

    console.warn("❌ Could not determine section name");
    return null;
  }

  /**
   * Replace grid markup (shared by string / section HTML paths).
   * Sets view-transition-name on the inner `[grid]` mount only (see `setFilterGridViewTransitionName`).
   */
  applyCollectionGridMarkup(html) {

    if (typeof html === "string") {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const newSection =
        doc.querySelector("section[data-filter-grid]") || doc.querySelector("[data-filter-grid]");

      if (newSection) {
        const parent = this.collectionGrid.parentElement;
        this.collectionGrid.outerHTML = newSection.outerHTML;
        this.collectionGrid =
          parent.querySelector("[data-filter-grid]") || document.querySelector("[data-filter-grid]");
      } else {
        console.warn("Could not find data-filter-grid element in new HTML, using innerHTML fallback");
        this.collectionGrid.innerHTML = html;
      }
    } else {
      this.collectionGrid.innerHTML = html;
    }

    this.setFilterGridViewTransitionName();

    this.reinitializeComponents();
  }

  updateCollectionGrid(html) {
    if (!html || !this.collectionGrid) {
      console.warn("updateCollectionGrid: Missing html or collectionGrid", {
        html: !!html,
        collectionGrid: !!this.collectionGrid,
      });
      this.hideLoadingState();
      return;
    }

    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    const shouldScrollToGridTop = this.isMobileOrTabletViewport();
    const scheduleRestoreScroll = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (shouldScrollToGridTop) {
            window.scrollTo(0, this.getCollectionGridScrollTop());
            return;
          }
          window.scrollTo(scrollX, scrollY);
        });
      });
    };

    const useViewTransition = this.prefersFilterViewTransition();

    const run = () => {
      try {
        this.applyCollectionGridMarkup(html);
      } catch (error) {
        console.error("Failed to update collection grid:", error);
      } finally {
        this.hideLoadingState();
        if (!useViewTransition) {
          scheduleRestoreScroll();
        }
      }
    };

    if (useViewTransition) {
      this.setFilterGridViewTransitionName();
      document.documentElement.classList.add("filter-results-vt");
      try {
        const vt = document.startViewTransition(run);
        vt.finished.finally(() => {
          document.documentElement.classList.remove("filter-results-vt");
          scheduleRestoreScroll();
        });
      } catch (e) {
        document.documentElement.classList.remove("filter-results-vt");
        run();
        scheduleRestoreScroll();
      }
    } else {
      run();
    }
  }

  reinitializeComponents() {
    try {

      if (!this.collectionGrid) {
        console.warn("Cannot reinitialize: collectionGrid is null");
        return;
      }

      // Re-initialize any grid-load-more elements in the updated section
      const gridLoadMoreElements = this.collectionGrid.querySelectorAll('grid-load-more');

      gridLoadMoreElements.forEach((element, index) => {
        try {
          // Force re-initialization if not already connected
          if (!element.grid || !element.loadMoreBtn) {
            // Trigger connectedCallback manually
            if (typeof element.connectedCallback === 'function') {
              element.connectedCallback();
            }
          }
        } catch (elemError) {
          console.warn(`Failed to reinitialize grid-load-more element ${index + 1}:`, elemError);
        }
      });

      // Re-initialize price range sliders
      const priceRangeElements = this.collectionGrid.querySelectorAll('price-range-slider');

      priceRangeElements.forEach((element, index) => {
        try {
          if (!element.rangeInputs || element.rangeInputs.length === 0) {
            if (typeof element.connectedCallback === 'function') {
              element.connectedCallback();
            }
          }
        } catch (elemError) {
          console.warn(`Failed to reinitialize price-range-slider element ${index + 1}:`, elemError);
        }
      });

      // Re-initialize any custom-filter elements
      const customFilterElements = this.collectionGrid.querySelectorAll('custom-filter');

      customFilterElements.forEach((element, index) => {
        try {
          if (!element.control || !element.body) {
            if (typeof element.connectedCallback === 'function') {
              element.connectedCallback();
            }
          }
        } catch (elemError) {
          console.warn(`Failed to reinitialize custom-filter element ${index + 1}:`, elemError);
        }
      });

    } catch (error) {
      console.warn('❌ Failed to reinitialize components:', error);
    }
  }

  attachSelectedFilterListeners() {
    if (!this.collectionGrid) {
      return;
    }

    const selectedFilterButtons = document.querySelectorAll('[selected-filter], .selected-filter[data-param-remove]');

    selectedFilterButtons.forEach((selected) => {
      if (selected.dataset.selectedFilterBound === 'true') {
        return;
      }

      selected.dataset.selectedFilterBound = 'true';
      selected.addEventListener('click', (event) => {
        this.handleSelectedFilterRemoval(event);
      });
    });
  }

  attachResetFilterListener() {
    if (!this.resetFilters.length || !this.collectionGrid) return;

    this.resetFilters.forEach((resetButton) => {
      if (resetButton.dataset.resetFiltersBound === 'true') return;
      resetButton.dataset.resetFiltersBound = 'true';
      resetButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.handleResetFilters();
      });
    });
  }

  async handleResetFilters() {
    if (!this.filterForm) return;

    const resettableFields = this.filterForm.querySelectorAll('input, select, textarea');

    resettableFields.forEach((field) => {
      const name = field.name || '';
      const shouldKeepField = name === 'sort_by' || name === 'q' || name === 'type';

      if (shouldKeepField || field.disabled) return;

      switch (field.type) {
        case 'checkbox':
        case 'radio':
          field.checked = false;
          break;
        case 'select-one':
          field.selectedIndex = 0;
          break;
        case 'select-multiple':
          Array.from(field.options).forEach((option) => {
            option.selected = false;
          });
          break;
        default:
          field.value = '';
      }
    });

    await this.handleFilterChange();
  }

  async handleSelectedFilterRemoval(event) {
    event.preventDefault();

    const selectedFilterButton = event.currentTarget;
    const paramRemoveString = selectedFilterButton?.getAttribute('data-param-remove');

    if (!paramRemoveString) {
      return;
    }

    this.clearLoadMoreState();
    this.showLoadingState();

    try {
      const currentUrl = new URL(window.location.href);
      const paramsToRemove = new URLSearchParams(paramRemoveString);

      paramsToRemove.forEach((value, key) => {
        this.removeSearchParamByValue(currentUrl.searchParams, key, value);
      });

      // Ensure we return to the first page after removing filters.
      currentUrl.searchParams.delete('page');

      await this.fetchAndRenderFromUrl(currentUrl);

      currentUrl.searchParams.delete('sections');
      window.history.pushState({}, '', currentUrl.toString());
    } catch (error) {
      console.error('Failed to remove selected filter:', error);
      this.hideLoadingState();
    }
  }

  removeSearchParamByValue(searchParams, key, valueToRemove) {
    const existingValues = searchParams.getAll(key);

    if (!existingValues.length) {
      return;
    }

    searchParams.delete(key);

    if (!valueToRemove || valueToRemove === '') {
      return;
    }

    existingValues.forEach((value) => {
      if (value !== valueToRemove) {
        searchParams.append(key, value);
      }
    });
  }

  async fetchAndRenderFromUrl(requestUrl) {
    const sectionName = this.getSectionName();
    const requestWithSection = new URL(requestUrl.toString());

    if (sectionName) {
      requestWithSection.searchParams.set('sections', sectionName);
    }

    const isLocalDev = window.location.hostname === '127.0.0.1' ||
      window.location.hostname === 'localhost' ||
      window.location.port !== '';

    let sectionsWorked = false;

    if (sectionName && !isLocalDev) {
      try {
        const response = await fetch(requestWithSection.toString(), {
          method: 'GET',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
          credentials: 'same-origin',
        });

        if (response.ok) {
          const data = await response.json();
          if (data && data[sectionName]) {
            this.updateCollectionGrid(data[sectionName]);
            sectionsWorked = true;
          }
        }
      } catch (error) {
        console.warn('Section render request failed during selected filter removal:', error);
      }
    }

    if (!sectionsWorked) {
      const fullPageUrl = new URL(requestWithSection.toString());
      fullPageUrl.searchParams.delete('sections');

      const fullResponse = await fetch(fullPageUrl.toString(), {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
      });

      if (!fullResponse.ok) {
        throw new Error(`HTTP error! status: ${fullResponse.status}`);
      }

      const html = await fullResponse.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newSection = doc.querySelector('[data-filter-grid]');
      const altSection = doc.querySelector('section[id*="collection"]') || doc.querySelector('section[id*="search"]');

      if (newSection) {
        this.updateCollectionGrid(newSection.outerHTML);
      } else if (altSection) {
        this.updateCollectionGrid(altSection.outerHTML);
      } else {
        throw new Error('Could not find suitable section in full page response');
      }
    }
  }

}

customElements.define('filter-container', Filters);